import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import KingBedOutlinedIcon from '@mui/icons-material/KingBedOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { useInhouseQrQuery, usePreRegBookingInfoQuery } from '../generated/graphql';
import { MainWrapper } from '../Components/MainWrapper';
import Loading from '../Components/Loading/Loading';
import { buildPreCheckInUrl } from '../Components/helper/preCheckInUrl';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import EmailIcon from '@mui/icons-material/Email';
import InboxIcon from '@mui/icons-material/Inbox';

const parsePasscode = (passCode?: string | null) => {
  if (!passCode) return null;
  try {
    return JSON.parse(passCode);
  } catch {
    return null;
  }
};

export default function Information() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const HotelID = searchParams.get('Hotel') as string;
  const BookingID = searchParams.get('Booking') as string;
  const location = useLocation();
  const localstate: any = location.state;

  const ORANGE = '#F5822B';
  const DARK_BLUE = '#394d86';
  const INFO_BG = '#F3F6FC';
  const INFO_BORDER = '#D9E2F2';

  const [tabIndex, setTabIndex] = useState(0);

  const {
    loading,
    data: { PreRegBookingInfo } = { PreRegBookingInfo: [] },
  } = usePreRegBookingInfoQuery({
    variables: {
      HotelID,
      BookingID,
      QR: 'true',
    },
    skip: !HotelID || !BookingID,
    fetchPolicy: 'no-cache',
  });

  const registrations = useMemo(() => {
    const fromQuery = PreRegBookingInfo?.result?.filter(
      (x: any) => x?.Registrations?.[0]?.RegistrationStatus === 'Inhouse'
    );
    if (fromQuery?.length) return fromQuery;
    return localstate?.Registrations || [];
  }, [PreRegBookingInfo, localstate?.Registrations]);

  const activeGroup = registrations?.[Math.min(tabIndex, Math.max(registrations.length - 1, 0))];
  const activeRegistration =
    activeGroup?.Registrations?.find((r: any) => r?.IsPrincipal) ||
    activeGroup?.Registrations?.[0];
  const passcodeParsed = parsePasscode(activeRegistration?.PassCode);

  const {
    loading: loadingQr,
    data: { InhouseQr } = { InhouseQr: null },
  } = useInhouseQrQuery({
    variables: {
      HotelID,
      BookingID,
      RegistrationID: activeRegistration?.ID,
    },
    skip: !HotelID || !BookingID || !activeRegistration?.ID,
    fetchPolicy: 'no-cache',
  });

  const tabLabel = (group: any, index: number) => {
    const reg =
      group?.Registrations?.find((r: any) => r?.IsPrincipal) ||
      group?.Registrations?.[0];
    return (
      reg?.Guest?.Contact?.FullName ||
      reg?.Room?.RoomNo ||
      (group?.RoomType ? `${group.RoomType}` : `Room ${index + 1}`)
    );
  };

  return (
    <MainWrapper>
      <Box
        sx={{ p: 2, width: '100%', maxWidth: '800px' }}
        justifyContent="center"
        alignItems="center"
        margin="auto"
        bgcolor="#F7FAF7"
        minHeight="90vh"
      >
        <Box display="flex" marginBottom="15px" alignItems="center">
          <IconButton
            style={{ padding: '0px', color: 'black' }}
            onClick={() => {
              if (HotelID && BookingID) {
                window.location.href = buildPreCheckInUrl(HotelID, BookingID);
              } else {
                navigate(-1);
              }
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <Typography fontSize="15px" fontWeight={600} paddingLeft="5px">
            Passcode Information
          </Typography>
        </Box>

        {loading ? (
          <Loading />
        ) : registrations?.length > 0 ? (
          <>
            <Tabs
              value={Math.min(tabIndex, registrations.length - 1)}
              onChange={(_, value) => setTabIndex(value)}
              variant="fullWidth"
              sx={{
                mb: '14px',
                minHeight: '42px',
                width: '100%',
                backgroundColor: '#fff',
                borderRadius: '4px',
                boxShadow: '0 1px 3px rgb(0 0 0 / 10%)',
                '& .MuiTabs-flexContainer': {
                  width: '100%',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: '42px',
                  minWidth: 0,
                  maxWidth: 'none',
                  flex: 1,
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#9e9e9e',
                  px: 1,
                },
                '& .Mui-selected': {
                  color: `${ORANGE} !important`,
                  fontWeight: 600,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: ORANGE,
                  height: '3px',
                },
                '& .MuiTab-wrapper, & .MuiTab-root': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {registrations.map((group: any, index: number) => (
                <Tab
                  key={group?.SharerNo || index}
                  label={tabLabel(group, index)}
                />
              ))}
            </Tabs>

            <Card
              sx={{
                mb: '14px',
                borderRadius: '10px',
                boxShadow:
                  '0 3px 1px -2px transparent, 2px 4px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)',
              }}
            >
              <CardContent sx={{ ':last-child': { p: '18px' } }}>
                <Typography
                  display="flex"
                  justifyContent="center"
                  fontSize="16px"
                  fontWeight={700}
                  sx={{ color: DARK_BLUE, mb: '14px' }}
                >
                  Access Passcodes
                </Typography>

                {activeRegistration?.Guest?.Contact?.FullName && (
                  <Typography
                    display="flex"
                    justifyContent="center"
                    fontSize="13px"
                    color="text.secondary"
                    mb="10px"
                  >
                    {activeRegistration.Guest.Contact.FullName}
                  </Typography>
                )}

                <Box display="flex" alignItems="center" py="10px">
                  <MeetingRoomOutlinedIcon sx={{ color: ORANGE, fontSize: '32px', mr: '16px' }} />
                  <Box>
                    <Typography fontSize="12px" color="text.secondary">
                      Main Entrance Door Passcode
                    </Typography>
                    <Typography
                      fontSize="22px"
                      fontWeight={700}
                      sx={{ color: '#1f2937', letterSpacing: '1px' }}
                    >
                      {passcodeParsed?.keyboardPwd
                        ? `${passcodeParsed?.publicDoorPasscode?.keyboardPwd}#`
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" alignItems="center" py="10px">
                  <KingBedOutlinedIcon sx={{ color: ORANGE, fontSize: '32px', mr: '16px' }} />
                  <Box>
                    <Typography fontSize="12px" color="text.secondary">
                      Room Passcode
                    </Typography>
                    <Typography
                      fontSize="22px"
                      fontWeight={700}
                      sx={{ color: '#1f2937', letterSpacing: '1px' }}
                    >
                      {passcodeParsed?.keyboardPwd
                        ? `${passcodeParsed.keyboardPwd}#`
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" alignItems="center" py="10px">
                  <ApartmentOutlinedIcon sx={{ color: ORANGE, fontSize: '32px', mr: '16px' }} />
                  <Box>
                    <Typography fontSize="14px" color="text.secondary">
                      Room Number:{' '}
                      <Box
                        component="span"
                        sx={{ color: DARK_BLUE, fontWeight: 700, fontSize: '16px' }}
                      >
                        {activeRegistration?.Room?.RoomNo || 'N/A'}
                      </Box>
                    </Typography>
                    <Typography fontSize="14px" color="text.secondary">
                      Location:{' '}
                      <Box
                        component="span"
                        sx={{ color: DARK_BLUE, fontWeight: 700, fontSize: '16px' }}
                      >
                        {activeRegistration?.Room?.Location?.Description || 'N/A'}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Box
              display="flex"
              alignItems="center"
              sx={{
                backgroundColor: INFO_BG,
                border: `1px solid ${INFO_BORDER}`,
                borderRadius: '8px',
                p: '10px 12px',
                mb: '10px',
              }}
            >
              <EmailIcon sx={{ color: ORANGE, fontSize: '20px', mr: '10px' }} />
              <Typography fontSize="12px" color="text.secondary">
                A welcome letter with your passcodes has been sent to{' '}
                <Box component="span" sx={{ color: '#2f6df6', fontWeight: 600 }}>
                  {activeRegistration?.Guest?.Contact?.Email}
                </Box>
              </Typography>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              sx={{
                backgroundColor: INFO_BG,
                border: `1px solid ${INFO_BORDER}`,
                borderRadius: '8px',
                p: '10px 12px',
                mb: '10px',
              }}
            >
              <InboxIcon sx={{ color: ORANGE, fontSize: '20px', mr: '10px' }} />
              <Typography fontSize="12px" color="text.secondary">
                Please check your Inbox, Spam, or Junk folder for the email.
              </Typography>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              sx={{
                backgroundColor: INFO_BG,
                border: `1px solid ${INFO_BORDER}`,
                borderRadius: '8px',
                p: '10px 12px',
                mb: '12px',
              }}
            >
              <SmartphoneOutlinedIcon sx={{ color: ORANGE, fontSize: '20px', mr: '10px' }} />
              <Typography fontSize="12px" color="text.secondary">
                You can also access your passcodes anytime via the{' '}
                <Box component="span" sx={{ color: DARK_BLUE, fontWeight: 700 }}>
                  HotelX Guest App
                </Box>
                .
              </Typography>
            </Box>

            <Typography
              fontSize="12px"
              color="text.secondary"
              textAlign="center"
              mb="10px"
            >
              Please click the QR code below to download our Guest App.
            </Typography>

            <Box display="flex" justifyContent="center" mb="16px">
              {loadingQr ? (
                <Loading />
              ) : InhouseQr?.qrCodeUrl ? (
                <a
                  href={InhouseQr?.qrPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block' }}
                >
                  <img
                    src={InhouseQr.qrCodeUrl}
                    alt="Guest App QR"
                    width={100}
                    height={100}
                    style={{ display: 'block' }}
                  />
                </a>
              ) : (
                <Typography fontSize="12px" color="text.secondary">
                  Guest App QR is unavailable.
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <Card
            sx={{
              mb: '14px',
              borderRadius: '4px',
              boxShadow:
                '0 3px 1px -2px transparent, 2px 5px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)',
            }}
          >
            <CardContent sx={{ ':last-child': { p: '14px' } }}>
              <Typography fontSize="13px" color="text.secondary">
                No checked-in registration passcodes are available yet.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </MainWrapper>
  );
}
